import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House, Users, Storefront, Package, Receipt, BookOpen,
  Percent,
  Gear, ShieldCheck, Bell, SignOut, CaretDown, ListChecks, List, X, Cube, Sparkle, Handshake,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Key } from "@phosphor-icons/react";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, roles: ["super_admin", "admin", "accounts", "operations", "sales"] },
  { to: "/ai", label: "AI Analyst", icon: Sparkle, roles: ["super_admin", "admin", "accounts", "operations", "sales"] },
  { to: "/orders", label: "Orders", icon: Package, roles: ["super_admin", "admin", "accounts", "operations", "sales"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["super_admin", "admin", "sales", "accounts", "operations"] },
  { to: "/suppliers", label: "Suppliers", icon: Storefront, roles: ["super_admin", "admin", "operations", "accounts"] },
  { to: "/brokers", label: "Brokers", icon: Handshake, roles: ["super_admin", "admin", "operations", "accounts"] },
  { to: "/items", label: "Items", icon: Cube, roles: ["super_admin", "admin", "operations", "sales"] },
  { to: "/payments", label: "Payments", icon: Receipt, roles: ["super_admin", "admin", "accounts"] },
  { to: "/ledger", label: "Ledger", icon: BookOpen, roles: ["super_admin", "admin", "accounts"] },
  { to: "/tax", label: "GST Report", icon: Percent, roles: ["super_admin", "admin", "accounts"] },
  { to: "/users", label: "Users", icon: ShieldCheck, roles: ["super_admin", "admin"] },
  { to: "/activity", label: "Activity Log", icon: ListChecks, roles: ["super_admin", "admin"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["super_admin", "admin"] },
];

const SidebarContent = ({ items, onNavigate, user }) => (
  <>
    <div className="px-5 py-5 border-b border-zinc-200">
      <div className="font-black tracking-tighter text-xl truncate" style={{ fontFamily: "Chivo" }}>
        Welcome, {user?.name}
      </div>
      <div className="label-mini mt-1">Operations Console</div>
    </div>
    <nav className="flex-1 overflow-y-auto py-2">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                isActive
                  ? "border-black bg-zinc-50 text-black font-semibold"
                  : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-black"
              }`
            }
          >
            <Icon size={16} weight="regular" />
            {it.label}
          </NavLink>
        );
      })}
    </nav>
    <div className="px-5 py-3 border-t border-zinc-200 text-[11px] text-zinc-500 tracking-tight">
      v1.1 · Multi-Brand
    </div>
  </>
);

const Sidebar = ({ items, user }) => (
  <aside className="hidden md:flex w-56 flex-col border-r border-zinc-200 bg-white h-screen sticky top-0" data-testid="sidebar">
    <SidebarContent items={items} user={user} />
  </aside>
);

const MobileSidebar = ({ items, open, onOpenChange, user }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="left" className="w-64 p-0 rounded-none" data-testid="mobile-sidebar">
      <div className="flex flex-col h-full bg-white">
        <SidebarContent items={items} user={user} onNavigate={() => onOpenChange(false)} />
      </div>
    </SheetContent>
  </Sheet>
);

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.put("/auth/me/password", { current_password: current, new_password: next });
      toast.success("Password changed");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="label-mini">Current Password</label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="current-password" />
          </div>
          <div>
            <label className="label-mini">New Password</label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="new-password" />
          </div>
          <div>
            <label className="label-mini">Confirm New Password</label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="confirm-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving || !current || !next || !confirm} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="save-password-btn">
            {saving ? "Saving…" : "Update Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TopBar = ({ onLogout, user, onOpenMobileNav }) => {
  const [activity, setActivity] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    if (open && (user.roles?.includes("admin") || user.roles?.includes("super_admin"))) {
      api.get("/activity?limit=15").then((r) => setActivity(r.data)).catch(() => {});
    }
  }, [open, user.roles]);

  return (
    <header className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 md:px-6 h-14 flex items-center justify-between gap-2" data-testid="topbar">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onOpenMobileNav}
          data-testid="mobile-menu-btn"
          className="md:hidden h-9 w-9 grid place-items-center border border-zinc-200 hover:bg-zinc-50 rounded-sm shrink-0"
          aria-label="Open menu"
        >
          <List size={18} />
        </button>
        <div className="md:hidden font-black tracking-tighter truncate" style={{ fontFamily: "Chivo" }}>
          Welcome, {user?.name}
        </div>
        <div className="hidden md:block text-xs text-zinc-500 tracking-tight">
          {new Date().toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <button data-testid="notification-bell" className="h-9 w-9 grid place-items-center border border-zinc-200 hover:bg-zinc-50 rounded-sm">
              <Bell size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 rounded-sm">
            <DropdownMenuLabel className="label-mini">Recent Activity</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {activity.length === 0 && (
              <div className="px-3 py-4 text-xs text-zinc-500">No recent activity.</div>
            )}
            {activity.slice(0, 10).map((a) => (
              <div key={a.id} className="px-3 py-2 text-xs border-b border-zinc-100 last:border-0">
                <div className="font-medium text-zinc-900">{a.description}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{a.user_name} · {new Date(a.timestamp).toLocaleString()}</div>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="flex items-center gap-2 h-9 px-3 border border-zinc-200 hover:bg-zinc-50 rounded-sm">
              <div className="h-6 w-6 grid place-items-center bg-black text-white text-[10px] font-bold">
                {(user?.name || "U")[0].toUpperCase()}
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-xs font-semibold leading-tight">{user?.name}</div>
                <div className="text-[10px] text-zinc-500 leading-tight">{(user?.roles || []).map((r) => ROLE_LABEL[r]).join(", ")}</div>
              </div>
              <CaretDown size={12} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44 rounded-sm">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="change-password-btn">
              <Key size={14} className="mr-2" /> Change Password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLogout} data-testid="logout-btn">
              <SignOut size={14} className="mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </header>
  );
};

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  const items = NAV.filter((n) => hasRole(...n.roles));
  return (
    <div className="flex min-h-screen bg-zinc-100">
      <Sidebar items={items} user={user} />
      <MobileSidebar items={items} open={mobileNavOpen} onOpenChange={setMobileNavOpen} user={user} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onLogout={handleLogout} user={user} onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main className="flex-1 p-4 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
